import { ReactNode, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button } from '@ai-tutor/ui';
import { ArrowRight } from 'lucide-react';

interface MindspaceDashboardCardProps {
  /** Card title */
  title: string;
  /** Card subtitle */
  subtitle: string;
  /** Illustration content (SVG or image) */
  children: ReactNode;
  /** Navigation path when clicking the button */
  actionPath: string;
  /** Button label */
  actionLabel: string;
  /** Optional className for custom styling */
  className?: string;
  /** Optional hover/focus prefetch hook. Fires once per mount the first
   *  time the user shows intent (mouseenter or focus) so the target
   *  page's data is in React Query cache before navigation. */
  onPrefetch?: () => void;
}

export function MindspaceDashboardCard({
  title,
  subtitle,
  children,
  actionPath,
  actionLabel,
  className = '',
  onPrefetch,
}: MindspaceDashboardCardProps) {
  const navigate = useNavigate();
  const prefetchedRef = useRef(false);

  const handleNavigate = () => {
    navigate(actionPath);
  };

  const triggerPrefetch = () => {
    if (prefetchedRef.current || !onPrefetch) return;
    prefetchedRef.current = true;
    onPrefetch();
  };

  return (
    <Card
      className={`group relative flex h-full flex-col overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-lg ${className}`}
      onMouseEnter={triggerPrefetch}
      onFocusCapture={triggerPrefetch}
    >
      <div className='flex h-full flex-col p-6'>
        {/* Header */}
        <div className='mb-4'>
          <h2 className='text-lg font-semibold text-foreground'>{title}</h2>
          <p className='text-sm text-muted-foreground'>{subtitle}</p>
        </div>

        {/* Illustration Area */}
        <div className='mb-4 flex min-h-0 flex-1 items-center justify-center'>
          {children}
        </div>

        {/* Action Button - Black */}
        <Button
          variant='default'
          size='lg'
          onClick={handleNavigate}
          className='w-full justify-center py-3 text-sm'
        >
          {actionLabel}
          <ArrowRight className='ml-2 h-4 w-4' />
        </Button>
      </div>
    </Card>
  );
}
